/**
 * ClockSettingsPanel.js
 * 
 * Control panel for customizing tournament clock display
 * - Toggle display elements on/off
 * - Configure prize structure display
 * - Customize colors and layout
 * - Add custom messages
 */

import React, { useState, useEffect } from 'react';

const ClockSettingsPanel = ({ tournamentId }) => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Prize structure editor
  const [prizePositions, setPrizePositions] = useState([]);
  const [newPosition, setNewPosition] = useState('');
  const [newPrize, setNewPrize] = useState('');

  const getApiUrl = () => {
    const hostname = window.location.hostname;
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return 'http://localhost:3000';
    } else if (hostname === '10.0.0.28') {
      return `https://${hostname}/api`;
    } else {
      return `https://${hostname}/api`;
    }
  };

  useEffect(() => {
    loadSettings();
  }, [tournamentId]);

  const loadSettings = async () => {
    try {
      const response = await fetch(`${getApiUrl()}/tournaments/${tournamentId}/clock-settings`);
      const data = await response.json();
      setSettings(data);
      
      // Load prize structure if exists
      if (data.prize_structure_data) {
        setPrizePositions(data.prize_structure_data);
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error loading settings:', error);
      setLoading(false);
    }
  };

  const updateSetting = (key, value) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${getApiUrl()}/tournaments/${tournamentId}/clock-settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...settings,
          prize_structure_data: prizePositions
        })
      });

      if (response.ok) {
        alert('Clock settings saved successfully!');
        loadSettings();
      } else {
        alert('Error saving settings');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('Failed to save settings');
    }
    setSaving(false);
  };

  const addPrizePosition = () => {
    if (!newPosition || !newPrize) {
      alert('Please enter both position and prize amount');
      return;
    }

    const position = {
      position: parseInt(newPosition),
      prize: parseFloat(newPrize)
    };

    setPrizePositions(prev => [...prev, position].sort((a, b) => a.position - b.position));
    setNewPosition('');
    setNewPrize('');
  };

  const removePrizePosition = (index) => {
    setPrizePositions(prev => prev.filter((_, i) => i !== index));
  };

  if (loading) {
    return <div className="container">Loading clock settings...</div>;
  }

  return (
    <div className="container" style={{ maxWidth: '1200px' }}>
      <h1>Clock Display Settings</h1>
      
      <div style={{ display: 'grid', gap: '20px' }}>
        
        {/* Display Elements */}
        <div className="card">
          <h3>Display Elements</h3>
          <p style={{ color: '#666', fontSize: '14px' }}>Toggle which elements appear on the tournament clock</p>
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '15px', marginTop: '15px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
              <input 
                type="checkbox"
                checked={settings.show_tournament_name}
                onChange={(e) => updateSetting('show_tournament_name', e.target.checked)}
              />
              <span>Tournament Name</span>
            </label>

            <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
              <input 
                type="checkbox"
                checked={settings.show_timer}
                onChange={(e) => updateSetting('show_timer', e.target.checked)}
              />
              <span>Timer / Clock</span>
            </label>

            <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
              <input 
                type="checkbox"
                checked={settings.show_level_info}
                onChange={(e) => updateSetting('show_level_info', e.target.checked)}
              />
              <span>Level Information</span>
            </label>

            <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
              <input 
                type="checkbox"
                checked={settings.show_blinds}
                onChange={(e) => updateSetting('show_blinds', e.target.checked)}
              />
              <span>Current Blinds</span>
            </label>

            <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
              <input 
                type="checkbox"
                checked={settings.show_next_blinds}
                onChange={(e) => updateSetting('show_next_blinds', e.target.checked)}
              />
              <span>Next Blinds</span>
            </label>

            <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
              <input 
                type="checkbox"
                checked={settings.show_player_count}
                onChange={(e) => updateSetting('show_player_count', e.target.checked)}
              />
              <span>Player Count</span>
            </label>

            <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
              <input 
                type="checkbox"
                checked={settings.show_average_stack}
                onChange={(e) => updateSetting('show_average_stack', e.target.checked)}
              />
              <span>Average Stack</span>
            </label>

            <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
              <input 
                type="checkbox"
                checked={settings.show_prize_pool}
                onChange={(e) => updateSetting('show_prize_pool', e.target.checked)}
              />
              <span>Prize Pool Amount</span>
            </label>

            <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
              <input 
                type="checkbox"
                checked={settings.show_total_entries}
                onChange={(e) => updateSetting('show_total_entries', e.target.checked)}
              />
              <span>Total Entries</span>
            </label>

            <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
              <input 
                type="checkbox"
                checked={settings.show_chips_in_play}
                onChange={(e) => updateSetting('show_chips_in_play', e.target.checked)}
              />
              <span>Total Chips in Play</span>
            </label>

            <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
              <input 
                type="checkbox"
                checked={settings.show_rebuys_addons}
                onChange={(e) => updateSetting('show_rebuys_addons', e.target.checked)}
              />
              <span>Rebuy/Addon Counts</span>
            </label>

            <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
              <input 
                type="checkbox"
                checked={settings.show_dealer_code}
                onChange={(e) => updateSetting('show_dealer_code', e.target.checked)}
              />
              <span>Dealer Code</span>
            </label>
          </div>
        </div>

        {/* Prize Structure */}
        <div className="card">
          <h3>Prize Structure Display</h3>
          
          <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', marginBottom: '15px' }}>
            <input 
              type="checkbox"
              checked={settings.show_prize_structure}
              onChange={(e) => updateSetting('show_prize_structure', e.target.checked)}
            />
            <span><strong>Show Prize Structure</strong></span>
          </label>

          {settings.show_prize_structure && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '20px' }}>
                <div className="form-group">
                  <label>Position on Screen:</label>
                  <select 
                    className="form-control"
                    value={settings.prize_structure_position}
                    onChange={(e) => updateSetting('prize_structure_position', e.target.value)}
                  >
                    <option value="top">Top</option>
                    <option value="bottom">Bottom</option>
                    <option value="left">Left Side</option>
                    <option value="right">Right Side</option>
                  </select>
                </div>

                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                    <input 
                      type="checkbox"
                      checked={settings.prize_structure_scroll}
                      onChange={(e) => updateSetting('prize_structure_scroll', e.target.checked)}
                    />
                    <span>Enable Scrolling Animation</span>
                  </label>
                </div>
              </div>

              {/* Prize Structure Editor */}
              <div style={{ background: '#f8f9fa', padding: '15px', borderRadius: '5px' }}>
                <h4>Configure Prizes</h4>
                
                <div style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
                  <input 
                    type="number"
                    className="form-control"
                    placeholder="Position (1st, 2nd, 3rd...)"
                    value={newPosition}
                    onChange={(e) => setNewPosition(e.target.value)}
                    style={{ width: '200px' }}
                  />
                  <input 
                    type="number"
                    step="0.01"
                    className="form-control"
                    placeholder="Prize Amount ($)"
                    value={newPrize}
                    onChange={(e) => setNewPrize(e.target.value)}
                    style={{ width: '200px' }}
                  />
                  <button className="btn btn-success" onClick={addPrizePosition}>
                    Add Position
                  </button>
                </div>

                {prizePositions.length > 0 && (
                  <div>
                    <h5>Current Prize Structure:</h5>
                    <table className="table" style={{ background: 'white' }}>
                      <thead>
                        <tr>
                          <th>Position</th>
                          <th>Prize</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {prizePositions.map((prize, index) => (
                          <tr key={index}>
                            <td>{getOrdinal(prize.position)}</td>
                            <td>${prize.prize.toFixed(2)}</td>
                            <td>
                              <button 
                                className="btn btn-danger"
                                onClick={() => removePrizePosition(index)}
                                style={{ fontSize: '12px', padding: '4px 8px' }}
                              >
                                Remove
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Custom Message */}
        <div className="card">
          <h3>Custom Message</h3>
          
          <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', marginBottom: '15px' }}>
            <input 
              type="checkbox"
              checked={settings.show_custom_message}
              onChange={(e) => updateSetting('show_custom_message', e.target.checked)}
            />
            <span><strong>Show Custom Message</strong></span>
          </label>

          {settings.show_custom_message && (
            <textarea 
              className="form-control"
              rows="3"
              placeholder="Enter custom message to display on clock..."
              value={settings.custom_message || ''}
              onChange={(e) => updateSetting('custom_message', e.target.value)}
            />
          )}
        </div>

        {/* Color Customization */}
        <div className="card">
          <h3>Color Customization</h3>
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '15px' }}>
            <div className="form-group">
              <label>Background Color:</label>
              <input 
                type="color"
                className="form-control"
                value={settings.background_color}
                onChange={(e) => updateSetting('background_color', e.target.value)}
              />
            </div>

            <div className="form-group">
              <label>Text Color:</label>
              <input 
                type="color"
                className="form-control"
                value={settings.text_color}
                onChange={(e) => updateSetting('text_color', e.target.value)}
              />
            </div>

            <div className="form-group">
              <label>Accent Color:</label>
              <input 
                type="color"
                className="form-control"
                value={settings.accent_color}
                onChange={(e) => updateSetting('accent_color', e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Save Button */}
        <div className="card" style={{ background: '#f8f9fa' }}>
          <button 
            className="btn btn-success" 
            onClick={saveSettings}
            disabled={saving}
            style={{ fontSize: '18px', padding: '12px 30px' }}
          >
            {saving ? 'Saving...' : '💾 Save Clock Settings'}
          </button>
          <p style={{ color: '#666', fontSize: '14px', marginTop: '10px', marginBottom: 0 }}>
            Changes will be applied immediately to the tournament clock display
          </p>
        </div>
      </div>
    </div>
  );
};

// Helper function to get ordinal suffix
const getOrdinal = (n) => {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

export default ClockSettingsPanel;